'use client';

import * as React from 'react';
import logo from '../../assets/images/logocic.png';
import { ArrowLeftStrokeIcon } from '@altus-ui/components_v2/icons';
import { ERROR_MESSAGES } from '../../types/errorTypes';
import { ErrorCode } from '../../types/errorTypes';
import { useGlobalClientContext } from '../common/GlobalClientContext';
import { appConfig } from '../../env/client';
import Image from 'next/image';

interface ErrorPageProps {
  statusCode?: number;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ statusCode }) => {
  const {
    i18nHelper: { t }
  } = useGlobalClientContext();

  const httpStatusCode = statusCode || 500;
  const errorInfoKey: ErrorCode = Object.keys(ERROR_MESSAGES).includes(
    httpStatusCode.toString()
  )
    ? (httpStatusCode as ErrorCode)
    : 500;

  const errorInfo = ERROR_MESSAGES[errorInfoKey];

  const handleGoHome = () => {
    const consoleBaseURL = appConfig().PUBLIC_SITE_URL;
    window.location.href = `${consoleBaseURL}`;
  };

  const handleGoBack = () => {
    window.history.back();
  };

  // Function to render text with styled emails
  const renderTextWithStyledEmails = (text: string) => {
    const emailPattern = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const parts = text.split(emailPattern);

    return parts.map((part, partIndex) => {
      if (emailPattern.test(part)) {
        return (
          <span key={partIndex} className="font-semibold text-blue-200">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // Function to render a single description line
  const renderDescriptionLine = (line: string, lineIndex: number) => (
    <p key={lineIndex} className="mb-2">
      {renderTextWithStyledEmails(line)}
    </p>
  );

  // Function to render description text with line breaks
  const renderDescriptionText = (key: string, index: number) => {
    const text = (t as any)(key, {}) || key;
    const lines = text.split('\n');

    return (
      <div key={index} className="w-max mx-auto m-2">
        {lines.map(renderDescriptionLine)}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center justify-center w-full h-[calc(100vh-64px)]">
      {/* Error card container with specific dimensions */}
      <div className="text-center flex flex-col items-center w-[768px] mx-auto">
        {/* Error code */}
        <div className="text-[#05A8FD] font-semibold font-body text-m leading-xl mb-4">
          {(t as any)('error.errorCode', { code: errorInfoKey })}
        </div>

        {/* Error title */}
        <h1 className="font-display font-semibold text-4xl !leading-[50px] text-gray-900 mb-4 max-w-[500px]">
          {(t as any)(errorInfo.titleKey, {})}
        </h1>

        {/* Error description */}
        <div className="text-gray-500 font-body font-normal !text-m w-auto mx-auto p-2">
          {errorInfo.descriptionKeys.map(renderDescriptionText)}
        </div>

        {/* Action buttons - side by side */}
        <div className="flex gap-4 mt-8">
          {/* Go Back button with arrow icon */}
          <button
            onClick={handleGoBack}
            className="w-[122px] h-[48px] bg-white text-gray-700 border-[1px] border-[#D0D5DD] hover:bg-gray-50 active:bg-gray-100 transition-colors rounded-lg font-medium !text-m leading-5 text-center flex items-center justify-center gap-1.5">
            <ArrowLeftStrokeIcon className="w-4 h-4" />
            {(t as any)('error.button.back', {}) || 'Go Back'}
          </button>

          {/* Go to Home button */}
          <button
            onClick={handleGoHome}
            className="w-[122px] h-[48px] bg-[#05A8FD] text-white hover:bg-[#0497E4] active:bg-[#0686C8] transition-colors rounded-lg font-medium !text-m leading-5 text-center flex items-center justify-center gap-1.5">
            {(t as any)('error.button.home', {}) || 'Go Home'}
          </button>
        </div>

        {/* CIC beta logo */}
        <div className="pt-[60px] px-[10px] pb-[10px] scale-75">
          <Image src={logo} alt="Logo" className="w-[242px]" />
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
